<template>
<global-modal v-model="isOpened" width="420px">
    <div class="login-modal">
        <img class="login-modal-logo" src="/gfx/header/logo_black.svg" width="150" height="25" alt="Pickawood">

        <template v-if="step === 'email'">
            <h2 class="login-modal-title">{{ text('login-title') }}</h2>
            <p v-if="socialError" class="login-modal-error">{{ text('social-error') }}</p>
            <p v-if="linkExpired" class="login-modal-error">{{ text('login-link-expired') }}</p>
            <p v-if="errorMessage" class="login-modal-error">{{ errorMessage }}</p>
            <p class="login-modal-text">{{ text('login-code-text') }}</p>
            <form @submit.prevent="submitEmail">
                <label class="login-modal-field">
                    <span>{{ text('field-email') }}</span>
                    <input v-model="form.email" type="email" name="email" autocomplete="email" required>
                </label>
                <button class="login-modal-submit" type="submit" :disabled="loading">{{ text('login-code-request') }}</button>
            </form>
            <p class="login-modal-terms" v-html="text('login-terms')"></p>
            <template v-if="config.social.length">
                <div class="login-modal-divider"><span>{{ text('login-social-divider') }}</span></div>
                <div class="login-modal-social">
                    <a v-for="item in config.social" :key="item.provider" :href="item.url" class="login-modal-social-button" :class="'login-modal-social-' + item.provider">
                        <svg v-if="item.provider === 'google'" width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                        <svg v-else-if="item.provider === 'facebook'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.12 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/>
                        </svg>
                        <svg v-else-if="item.provider === 'apple'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M17.05 12.54c-.03-2.73 2.23-4.04 2.33-4.11-1.27-1.86-3.25-2.11-3.95-2.14-1.68-.17-3.28 1-4.13 1-.85 0-2.16-.98-3.55-.95-1.83.03-3.51 1.06-4.45 2.7-1.9 3.29-.48 8.16 1.36 10.83.9 1.31 1.98 2.77 3.39 2.72 1.36-.06 1.88-.88 3.53-.88 1.64 0 2.11.88 3.55.85 1.47-.03 2.4-1.33 3.3-2.64 1.04-1.51 1.47-2.98 1.49-3.05-.03-.01-2.86-1.1-2.89-4.33zM14.4 4.42c.75-.91 1.25-2.17 1.11-3.42-1.08.04-2.38.72-3.15 1.62-.69.8-1.29 2.08-1.13 3.31 1.2.09 2.43-.61 3.17-1.51z"/>
                        </svg>
                        <span>{{ item.label }}</span>
                    </a>
                </div>
            </template>
        </template>

        <template v-else>
            <h2 class="login-modal-title">{{ text('login-code-title') }}</h2>
            <p class="login-modal-text">{{ codeSentText }}</p>
            <p v-if="resent" class="login-modal-success">{{ text('login-code-resent') }}</p>
            <p v-if="errorMessage" class="login-modal-error">{{ errorMessage }}</p>
            <form @submit.prevent="submitCode">
                <label class="login-modal-field">
                    <span>{{ text('login-code-label') }}</span>
                    <input
                        ref="codeInput"
                        v-model="form.code"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        class="login-modal-code"
                        required
                    >
                </label>
                <span v-if="errors.code" class="login-modal-field-error">{{ errors.code[0] }}</span>
                <button class="login-modal-submit" type="submit" :disabled="loading">{{ text('login-code-submit') }}</button>
            </form>
            <div class="login-modal-links">
                <a href="#" @click.prevent="backToEmail">{{ text('login-code-back') }}</a>
                <a href="#" @click.prevent="resend">{{ text('login-code-resend') }}</a>
            </div>
        </template>
    </div>
</global-modal>
</template>

<script setup lang="ts">
import {GlobalModal} from '@webstacz/shop-components';
import {computed, nextTick, reactive, ref, useTemplateRef} from 'vue';
import axios from '@/plugins/axios';

export type LoginModalConfig = {
    urls: {
        loginCodeRequest: string;
        loginCodeVerify: string;
        csrf: string;
        accountProducts?: string;
    };
    social: Array<{ provider: string; label: string; url: string }>;
    texts: Record<string, string>;
};

const props = defineProps<{ config: LoginModalConfig }>();

const isOpened = ref(false);
const step = ref<'email' | 'code'>('email');
const loading = ref(false);
const resent = ref(false);
const socialError = ref(false);
const linkExpired = ref(false);
const errorMessage = ref('');
const errors = reactive<Record<string, string[]>>({});
const codeInputRef = useTemplateRef<HTMLInputElement>('codeInput');
const redirectAfterLogin = ref(true);

const form = reactive({
    email: '',
    code: '',
});

const text = (key: string): string => props.config.texts[key] ?? key;

const codeSentText = computed((): string => text('login-code-text-sent').replace(':email', form.email));

const clearFeedback = (): void => {
    errorMessage.value = '';
    resent.value = false;
    Object.keys(errors).forEach((key) => delete errors[key]);
};

const refreshCsrf = async (): Promise<void> => {
    try {
        const {data} = await axios.post(props.config.urls.csrf);
        const token = typeof data === 'string' ? (data.match(/^[A-Za-z0-9]+/)?.[0] ?? '') : '';
        if (token.length) {
            axios.defaults.headers.common['X-CSRF-TOKEN'] = token;
            const meta = document.head.querySelector('meta[name="csrf-token"]');
            if (meta) {
                meta.setAttribute('content', token);
            }
        }
    } catch (e) {
        console.error('CSRF refresh failed', e);
    }
};

window.openLoginModal = (options = {}): void => {
    step.value = 'email';
    form.code = '';
    socialError.value = options.socialError ?? false;
    redirectAfterLogin.value = options.redirectAfterLogin ?? true;
    linkExpired.value = options.linkExpired ?? false;
    clearFeedback();
    if (options.prefillEmail) {
        form.email = options.prefillEmail;
    }
    isOpened.value = true;
    refreshCsrf();
};

const postWithCsrfRetry = async (url: string, payload: object): Promise<any> => {
    try {
        return await axios.post(url, payload);
    } catch (e: any) {
        if (e?.response?.status === 419) {
            await refreshCsrf();
            return await axios.post(url, payload);
        }
        throw e;
    }
};

const handleError = (e: any): void => {
    if (e?.response?.status === 422 && e.response.data?.errors) {
        Object.assign(errors, e.response.data.errors);
        errorMessage.value = errors.code ? '' : (e.response.data.message ?? '');
        return;
    }
    errorMessage.value = text('error-generic');
};

const needsReloadAfterLogin = (): boolean => {
    // the checkout SPA renders its state (address prefill included) from the
    // server payload built at page load, so it has to be re-rendered
    return document.getElementById('checkout2') !== null;
};

/**
 * The answer is deliberately the same whether or not the address has an
 * account, so this always advances to the code step.
 *
 * @returns {Promise<void>}
 */
const submitEmail = async (): Promise<void> => {
    clearFeedback();
    loading.value = true;
    try {
        await postWithCsrfRetry(props.config.urls.loginCodeRequest, {email: form.email});
        step.value = 'code';
        await nextTick();
        codeInputRef.value?.focus();
    } catch (e) {
        handleError(e);
    } finally {
        loading.value = false;
    }
};

const resend = async (): Promise<void> => {
    clearFeedback();
    loading.value = true;
    try {
        await postWithCsrfRetry(props.config.urls.loginCodeRequest, {email: form.email});
        resent.value = true;
    } catch (e) {
        handleError(e);
    } finally {
        loading.value = false;
    }
};

const backToEmail = (): void => {
    step.value = 'email';
    form.code = '';
    clearFeedback();
};

const submitCode = async (): Promise<void> => {
    clearFeedback();
    loading.value = true;
    try {
        await postWithCsrfRetry(props.config.urls.loginCodeVerify, {email: form.email, code: form.code});

        if (needsReloadAfterLogin()) {
            window.location.reload();
            return;
        }

        if (redirectAfterLogin.value && props.config.urls.accountProducts) {
            window.location.assign(props.config.urls.accountProducts);
            return;
        }

        // logging in regenerates the session and with it the CSRF token, so
        // without this every write that follows on this page answers 419
        await refreshCsrf();

        isOpened.value = false;
        document.dispatchEvent(new CustomEvent('customerAuthChanged'));
    } catch (e) {
        handleError(e);
    } finally {
        loading.value = false;
    }
};
</script>

<style scoped lang="scss">
// GlobalModal ships 50px of padding of its own; the dialog is narrow and the
// form needs the room, so the spacing is owned here instead
:deep(.global-modal-body) {
    padding: 0;
}

.login-modal {
    padding: 22px 24px 24px;
    text-align: center;

    @media (max-width: 767px) {
        padding: 20px 16px 20px;
    }

    .login-modal-logo {
        display: block;
        width: 140px;
        height: auto;
        margin: 0 auto 16px;
    }

    .login-modal-title {
        margin: 0 0 18px;
        font-size: 20px;
        line-height: 26px;
    }

    .login-modal-text {
        margin: 0 0 18px;
        font-size: 14px;
        line-height: 20px;
        color: #555555;
    }

    .login-modal-error,
    .login-modal-success {
        margin: 0 0 16px;
        padding: 10px 14px;
        font-size: 13px;
        line-height: 18px;
        text-align: left;
    }

    .login-modal-error {
        color: #cc2f04;
        background: #fdeceb;
    }

    .login-modal-success {
        color: #1d8a4e;
        background: #e4f6ef;
    }

    form {
        text-align: left;
    }

    .login-modal-field {
        display: block;
        margin: 0 0 12px;

        span {
            display: block;
            margin: 0 0 4px;
            padding-left: 4px;
            font-size: 13px;
            font-weight: 300;
        }

        input {
            width: 100%;
            height: 46px;
            padding: 0 14px;
            border: 1px solid #cccccc;
            font-size: 15px;

            &:focus {
                border-color: #000000;
                outline: none;
            }
        }
    }

    .login-modal-field-error {
        display: block;
        margin: -8px 0 10px;
        padding-left: 4px;
        color: #cc2f04;
        font-size: 12px;
        text-align: left;
    }

    .login-modal-code {
        font-size: 24px;
        font-weight: 600;
        letter-spacing: 10px;
        text-align: center;
    }

    .login-modal-terms {
        margin: 12px 0 0;
        font-size: 12px;
        line-height: 18px;
        color: #888888;

        a {
            color: #888888;
            text-decoration: underline;
        }
    }

    .login-modal-checkbox {
        display: flex;
        gap: 8px;
        align-items: flex-start;
        margin: 4px 0 18px;
        font-size: 13px;
        line-height: 18px;
        text-align: left;
    }

    .login-modal-submit {
        width: 100%;
        margin: 6px 0 0;
        padding: 16px;
        border: 0;
        background: #000000;
        font-size: 15px;
        font-weight: bold;
        line-height: 20px;
        text-transform: uppercase;
        color: #ffffff;
        cursor: pointer;
        transition: background .2s, color .2s;

        &:hover {
            background: #ffd24d;
            color: #000000;
        }

        &:disabled {
            background: #e5e5e5;
            color: #ffffff;
            cursor: default;
        }
    }

    .login-modal-links {
        display: flex;
        justify-content: center;
        gap: 20px;
        margin: 18px 0 0;
        font-size: 13px;

        a {
            color: #555555;
            text-decoration: underline;

            &:hover {
                color: #000000;
            }
        }
    }

    .login-modal-divider {
        position: relative;
        margin: 22px 0 16px;
        font-size: 12px;
        letter-spacing: 1px;
        text-transform: uppercase;
        color: #999999;

        &:before {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            border-top: 1px solid #ececec;
        }

        span {
            position: relative;
            padding: 0 12px;
            background: #ffffff;
        }
    }

    .login-modal-social {
        display: flex;
        flex-direction: column;
        gap: 8px;

        // each provider wears its own colours, as their brand guidelines ask and
        // as visitors expect from a sign in button
        .login-modal-social-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 12px;
            border: 1px solid #dddddd;
            text-align: center;
            font-size: 14px;
            font-weight: 600;
            color: #222222;
            text-decoration: none;

            svg {
                flex: 0 0 auto;
            }
        }

        .login-modal-social-google {
            border-color: #747775;
            background: #ffffff;
            color: #1f1f1f;

            &:hover {
                background: #f7f8f8;
            }
        }

        .login-modal-social-facebook {
            border-color: #1877f2;
            background: #1877f2;
            color: #ffffff;

            &:hover {
                border-color: #166fe0;
                background: #166fe0;
            }
        }

        .login-modal-social-apple {
            border-color: #000000;
            background: #000000;
            color: #ffffff;

            &:hover {
                border-color: #1a1a1a;
                background: #1a1a1a;
            }
        }
    }
}
</style>
